// ============================================
// Path: /frontend/src/components/ui/Pagination.jsx
// Purpose: Reusable pagination component
// ============================================

import { Button } from './button';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export function Pagination({ 
  currentPage, 
  totalPages, 
  onPageChange,
  totalItems = 0,
  itemsPerPage = 12,
  showInfo = true 
}) {
  
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      onPageChange(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Calculate range of items being shown
  const startItem = ((currentPage - 1) * itemsPerPage) + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  // Don't render if only 1 page
  if (totalPages <= 1) return null;

  return (
    <div className="space-y-4">
      {/* Info text */}
      {showInfo && totalItems > 0 && (
        <div className="flex justify-between items-center text-sm text-muted-foreground">
          <p>
            Showing {startItem} - {endItem} of {totalItems} items
          </p>
          <p>Page {currentPage} of {totalPages}</p>
        </div>
      )}

      {/* Pagination controls */}
      <div className="flex items-center justify-between">
        {/* Previous button */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Previous
        </Button>

        {/* Page numbers */}
        <div className="flex gap-2">
          {/* First page */}
          {currentPage > 2 && (
            <>
              <Button
                variant={currentPage === 1 ? 'default' : 'outline'}
                size="sm"
                onClick={() => goToPage(1)}
              >
                1
              </Button>
              {currentPage > 3 && (
                <span className="px-2 flex items-center text-muted-foreground">...</span>
              )}
            </>
          )}

          {/* Pages around current */}
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(page => 
              page === currentPage ||
              page === currentPage - 1 ||
              page === currentPage + 1
            )
            .map(page => (
              <Button
                key={page}
                variant={currentPage === page ? 'default' : 'outline'}
                size="sm"
                onClick={() => goToPage(page)}
              >
                {page}
              </Button>
            ))}

          {/* Last page */}
          {currentPage < totalPages - 1 && (
            <>
              {currentPage < totalPages - 2 && (
                <span className="px-2 flex items-center text-muted-foreground">...</span>
              )}
              <Button
                variant={currentPage === totalPages ? 'default' : 'outline'}
                size="sm"
                onClick={() => goToPage(totalPages)}
              >
                {totalPages}
              </Button>
            </>
          )}
        </div>

        {/* Next button */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
          <ChevronRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}